import React, { useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container, Box, Button } from '@mui/material'
import { useSelector } from 'react-redux'
import { getAuthPermissions, PERM_DELETE_CONTENT, PERM_UPDATE_CONTENT } from 'services/auth'
import { selectAuth } from 'services/auth/redux/selectors'
import { selectBrooklynServerUrl, selectConfig } from 'services/config/redux/selectors'
import { getIdFromV2Location, removeContent } from 'services/content'
import { CONTENT_TYPE_PERSON } from 'services/content/content-types'
import { selectLocation } from 'services/location/redux/selectors'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ConfirmDialog from 'gui/components/common/ConfirmDialog'
import ContentDetailQueryStates from './ContentDetailQueryStates'
import ContentInformationSection from './ContentInformationSection'
import FilterTypeSection from './FilterTypeSection'
import VideoContentReferencesSection from './VideoContentReferencesSection'
import VideoContentReferencesExtendedSection from './VideoContentReferencesExtendedSection'
import VideoContentReferencesYoreSection from './VideoContentReferencesYoreSection'
import AnonymousFieldsSection from './AnonymousFieldsSection'
import { GET_VIDEO_CONTENT, UPDATE_VIDEO_CONTENT } from './queries'
import ContentImagesSection from './content-detail-images/content-images-section'
import FloatingSaveCancelButtons from './FloatingSaveCancelButtons'
import ContentDetailSnackbar, { useContentDetailSnackbar } from './ContentDetailSnackbar'
import NewEditorBanner from './LegacyEditorBanner'
import { useSectionManagedSlot } from './useSectionManagedSlot'
import type { ContentInfoChanges } from './types'
import './course-detail.scss'

export interface PersonDetailV2Props {
  /** When provided, use this id instead of reading from V2 location */
  id?: string | number
}

/**
 * Person Detail V2 Component
 * GraphQL-powered detail page for person content at /v2/content/:id.
 * Same sections as VideoDetailV2 (person always shows anonymous fields).
 */
function PersonDetailV2 (props: PersonDetailV2Props = {}) {
  const { id: idProp } = props
  const location = useSelector(selectLocation)
  const auth = useSelector(selectAuth)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)
  const canDelete = authPermissions.includes(PERM_DELETE_CONTENT)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)
  const config = useSelector(selectConfig)
  const enableMarketingCenters = config?.getIn?.(['data', 'features', 'enableMarketingCenters'])

  const idFromLocation = getIdFromV2Location(location)
  const id = idProp !== undefined && idProp !== null ? idProp : idFromLocation

  const { loading, error, data, refetch } = useQuery(GET_VIDEO_CONTENT, {
    variables: { id },
    skip: !id,
  })

  const personData = data?.contentList?.content?.[0] ?? null

  // Parent-managed: section reports changes up, parent owns mutation
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)
  const [filterTypeDirty, setFilterTypeDirty] = useState<boolean>(false)
  const [filterTypeChanges, setFilterTypeChanges] = useState<ContentInfoChanges | null>(null)

  // Section-managed: sections own save; useSectionManagedSlot bundles dirty + saveHandlerRef per section
  const contentRefs = useSectionManagedSlot()
  const contentRefsExtended = useSectionManagedSlot()
  const contentRefsYore = useSectionManagedSlot()
  const anonSlot = useSectionManagedSlot()

  const [resetTrigger, setResetTrigger] = useState<number>(0)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)

  const isDirty =
    contentInfoDirty ||
    filterTypeDirty ||
    contentRefs.dirty ||
    contentRefsExtended.dirty ||
    contentRefsYore.dirty ||
    anonSlot.dirty

  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  const handleFilterTypeDirtyChange = useCallback((dirty: boolean) => {
    setFilterTypeDirty(dirty)
  }, [])

  const handleFilterTypeChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setFilterTypeChanges(changes)
  }, [])

  const [updateVideoContent, { loading: saving }] = useMutation(UPDATE_VIDEO_CONTENT, {
    refetchQueries: [{ query: GET_VIDEO_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useContentDetailSnackbar()

  const handleSave = async () => {
    try {
      if (
        !contentInfoChanges &&
        !filterTypeChanges &&
        !contentRefs.dirty &&
        !contentRefsExtended.dirty &&
        !contentRefsYore.dirty &&
        !anonSlot.dirty
      ) {
        showSnackbar('No changes to save', 'info')
        return
      }

      let combinedChanges: ContentInfoChanges | null = null
      const parentManagedChanges = [contentInfoChanges, filterTypeChanges].filter(
        Boolean,
      ) as ContentInfoChanges[]
      if (parentManagedChanges.length > 0) {
        combinedChanges = {
          contentInput: Object.assign({}, ...parentManagedChanges.map((c) => c.contentInput)),
          generatePath: parentManagedChanges.some((c) => c.generatePath),
        }
      }

      if (combinedChanges) {
        await updateVideoContent({
          variables: {
            content: combinedChanges.contentInput,
            generatePath: combinedChanges.generatePath ?? false,
          },
        })
      }

      if (contentRefs.dirty && contentRefs.saveHandlerRef.current) {
        await contentRefs.saveHandlerRef.current()
      }

      if (contentRefsExtended.dirty && contentRefsExtended.saveHandlerRef.current) {
        await contentRefsExtended.saveHandlerRef.current()
      }

      if (contentRefsYore.dirty && contentRefsYore.saveHandlerRef.current) {
        await contentRefsYore.saveHandlerRef.current()
      }

      if (anonSlot.dirty && anonSlot.saveHandlerRef.current) {
        await anonSlot.saveHandlerRef.current()
      }

      await refetch()
      setFilterTypeDirty(false)
      setFilterTypeChanges(null)
      showSnackbar('Content saved successfully!', 'success')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      showSnackbar(`Error saving: ${errorMessage}`, 'error')
    }
  }

  const handleCancel = () => {
    setFilterTypeDirty(false)
    setFilterTypeChanges(null)
    setResetTrigger((prev) => prev + 1)
    showSnackbar('Changes discarded', 'info')
  }

  const openDeleteConfirm = useCallback(() => setDeleteConfirmOpen(true), [])
  const closeDeleteConfirm = useCallback(() => setDeleteConfirmOpen(false), [])

  const handleDeleteConfirm = useCallback(async () => {
    const contentIdForDelete = personData?.id ?? id
    if (contentIdForDelete == null) return
    await removeContent({
      auth,
      server: brooklynUrl,
      contentId: String(contentIdForDelete),
    })
    window.location.replace('/content')
  }, [auth, brooklynUrl, personData?.id, id])

  return (
    <ContentDetailQueryStates
      loading={loading}
      error={error}
      hasData={!!personData}
      errorTitle="Error loading person"
      notFoundTitle="Person not found"
      notFoundDetail={`No person found with ID: ${id}`}
    >
      <Container className="course-detail content-detail" maxWidth={false}>
        {id != null ? <NewEditorBanner contentId={id} /> : null}
        <Container className="content-detail__section content-information" maxWidth={false}>
          <ContentInformationSection
            contentData={personData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleContentInfoDirtyChange}
            onChangesUpdate={handleContentInfoChangesUpdate}
            onResetTriggered={resetTrigger}
          />
        </Container>

        <AnonymousFieldsSection
          contentData={personData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={anonSlot.onDirtyChange}
          saveHandlerRef={anonSlot.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <Container className="content-detail__section content-field-type" maxWidth={false}>
          <FilterTypeSection
            contentType={CONTENT_TYPE_PERSON}
            contentData={personData}
            isViewOnly={isViewOnlyMode}
            isMulti={true}
            bundleDisabled={true}
            enableMarketingCenters={enableMarketingCenters}
            onDirtyChange={handleFilterTypeDirtyChange}
            onChangesUpdate={handleFilterTypeChangesUpdate}
            onResetTriggered={resetTrigger}
          />
        </Container>

        <VideoContentReferencesSection
          contentData={personData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefs.onDirtyChange}
          saveHandlerRef={contentRefs.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <VideoContentReferencesExtendedSection
          contentData={personData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsExtended.onDirtyChange}
          saveHandlerRef={contentRefsExtended.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <VideoContentReferencesYoreSection
          contentData={personData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsYore.onDirtyChange}
          saveHandlerRef={contentRefsYore.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <Container className="content-detail__section content-images" maxWidth={false}>
          <SectionAccordion title="Images" collapsed={false}>
            <ContentImagesSection
              contentId={personData?.contentId}
              contentType={personData?.contentType}
              language={personData?.language}
              primaryLanguage={personData?.primaryLanguage}
            />
          </SectionAccordion>
        </Container>

        <Container
          className="content-detail__section content-actions-delete-section"
          maxWidth={false}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="error"
              onClick={openDeleteConfirm}
              disabled={isViewOnlyMode || saving || !canDelete || !personData}
              sx={{ minWidth: '120px', height: '36px', fontSize: '14px' }}
            >
              Delete
            </Button>
          </Box>
        </Container>

        {!isViewOnlyMode && (
          <FloatingSaveCancelButtons
            visible={isDirty}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )}

        <ContentDetailSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        />

        <ConfirmDialog
          open={deleteConfirmOpen}
          onClose={closeDeleteConfirm}
          title="Delete Content"
          description={`Are you sure you want to delete "${(personData?.title ?? '').trim() || 'this person'}"?`}
          onConfirm={handleDeleteConfirm}
          confirmLabel="Delete"
          busyLabel="Deleting..."
          severity="error"
        />
      </Container>
    </ContentDetailQueryStates>
  )
}

export default PersonDetailV2
